import React from 'react';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { boolean, mixed, number, object, string } from 'yup';

import { TId } from '@/features/common';
import { IPipelineCategory } from '@/features/pipeline';

import { EAutomationType } from '../../types';

import {
  EAutomationGroup,
  EAutomationCategory,
  TAutomationSettings,
} from './automations-wizard.types';
import * as lazy from './ui';

const AUTOMATION_SETTINGS: Record<EAutomationType, TAutomationSettings> = {
  [EAutomationType.SendTestInviteToMovedCandidate]: {
    type: EAutomationType.SendTestInviteToMovedCandidate,
    group: EAutomationGroup.InviteToTest,
    category: EAutomationCategory.Test,
    icon: regular('envelope-open-text'),
    label: 'When a candidate is moved to this stage, invite them to the test',
    settings: [
      'When a candidate is moved to this stage, invite them to the test by email:',
      <lazy.EmailTemplateSelect key="email-template-select" />,
    ],
    confirmation: [
      'When a candidate is moved to this stage, invite them to the test by email - ',
      <lazy.EmailTemplateName key="email-template-name" />,
    ],
    requiredFields: { templateId: true },
  },
  [EAutomationType.SentEmailToAppliedCandidate]: {
    type: EAutomationType.SentEmailToAppliedCandidate,
    group: EAutomationGroup.SendEmail,
    category: EAutomationCategory.Applied,
    icon: regular('envelope'),
    label: 'When a candidate applies, send them an email',
    settings: [
      'When a candidate applies, send them an email:',
      <lazy.EmailTemplateSelect key="email-template-select" />,
    ],
    confirmation: [
      'When a candidate applies, send them an email - ',
      <lazy.EmailTemplateName key="email-template-name" />,
    ],
    requiredFields: { templateId: true },
  },
  [EAutomationType.SendEmailToMovedCandidate]: {
    type: EAutomationType.SendEmailToMovedCandidate,
    group: EAutomationGroup.SendEmail,
    category: EAutomationCategory.All,
    icon: regular('envelope'),
    label: 'When a candidate is moved to this stage, send them an email',
    settings: [
      'When a candidate is moved to this stage, send them an email:',
      <lazy.EmailTemplateSelect key="email-template-select" />,
    ],
    confirmation: [
      'When a candidate is moved to this stage, send them an email - ',
      <lazy.EmailTemplateName key="email-template-name" />,
    ],
    requiredFields: { templateId: true },
  },
  [EAutomationType.SendTestReminder]: {
    type: EAutomationType.SendTestReminder,
    group: EAutomationGroup.SendEmail,
    category: EAutomationCategory.Test,
    icon: regular('envelope'),
    label: 'Remind candidates to take the test',
    settings: [
      'Send a reminder to candidates',
      <lazy.TimeInput key="time-input" />,
      'after you invite them to take the test',
    ],
    confirmation: [
      'Send a reminder to candidates ',
      <lazy.TimeValue key="time-value" />,
      ' after you invite them to take the test',
    ],
    requiredFields: { value: true },
  },
  [EAutomationType.MoveAppliedCandidate]: {
    type: EAutomationType.MoveAppliedCandidate,
    group: EAutomationGroup.Move,
    category: EAutomationCategory.Applied,
    icon: regular('arrow-right'),
    label: 'When a candidate applies, move them to a different stage',
    settings: [
      'When a candidate applies, move them to a different pipeline stage:',
      <lazy.PipelineStageSelect key="pipeline-stage-select" />,
    ],
    confirmation: [
      'When a candidate applies, move them to the ',
      <lazy.PipelineStageName key="pipeline-stage-name" />,
      ' stage',
    ],
    requiredFields: { destinationStageId: true },
  },
  [EAutomationType.MoveOverThresholdCandidate]: {
    type: EAutomationType.MoveOverThresholdCandidate,
    group: EAutomationGroup.MoveBasedOnScore,
    category: EAutomationCategory.Test,
    icon: regular('arrow-right'),
    label:
      'When a candidate scores more than XX %, move them to a different stage',
    settings: [
      'When a candidate scores more than',
      <lazy.ScoreInput key="score-input" />,
      'move them to the',
      <lazy.PipelineStageSelect key="pipeline-stage-select" />,
      'stage',
    ],
    confirmation: [
      'When a candidate scores more than ',
      <lazy.ScoreValue key="score-value" />,
      ', move them to the ',
      <lazy.PipelineStageName key="pipeline-stage-name" />,
      ' stage',
    ],
    requiredFields: { value: true, destinationStageId: true },
  },
  [EAutomationType.MoveUnderThresholdCandidate]: {
    type: EAutomationType.MoveUnderThresholdCandidate,
    group: EAutomationGroup.MoveBasedOnScore,
    category: EAutomationCategory.Test,
    icon: regular('arrow-right'),
    label:
      'When a candidate scores less than XX %, move them to a different stage',
    settings: [
      'When a candidate scores less than',
      <lazy.ScoreInput key="score-input" />,
      'move them to the',
      <lazy.PipelineStageSelect key="pipeline-stage-select" />,
      'stage',
    ],
    confirmation: [
      'When a candidate scores less than ',
      <lazy.ScoreValue key="score-value" />,
      ', move them to the ',
      <lazy.PipelineStageName key="pipeline-stage-name" />,
      ' stage',
    ],
    requiredFields: { value: true, destinationStageId: true },
  },
  [EAutomationType.RejectUnderThresholdCandidate]: {
    type: EAutomationType.RejectUnderThresholdCandidate,
    group: EAutomationGroup.RejectBasedOnScore,
    category: EAutomationCategory.Test,
    icon: regular('thumbs-down'),
    label: 'When a candidate scores less than XX %, mark them as rejected',
    settings: [
      'When a candidate scores less than',
      <lazy.ScoreInput key="score-input" />,
      'mark them as',
      <lazy.RejectedTag key="rejected-tag" />,
    ],
    confirmation: [
      'When a candidate scores less than ',
      <lazy.ScoreValue key="score-value" />,
      ', mark them as rejected',
    ],
    requiredFields: { value: true },
  },
  [EAutomationType.SendRejectionToUnderThresholdCandidate]: {
    type: EAutomationType.SendRejectionToUnderThresholdCandidate,
    group: EAutomationGroup.RejectBasedOnScore,
    category: EAutomationCategory.Test,
    icon: regular('thumbs-down'),
    label:
      'When a candidate scores less than XX %, mark them as rejected and send them a rejection email',
    settings: [
      'When a candidate scores less than',
      <lazy.ScoreInput key="score-input" />,
      'mark them as',
      <lazy.RejectedTag key="rejected-tag" />,
      'and send them an email:',
      <lazy.EmailTemplateSelect key="email-template-select" />,
    ],
    confirmation: [
      'When a candidate scores less than ',
      <lazy.ScoreValue key="score-value" />,
      ', mark them as rejected and send them an email - ',
      <lazy.EmailTemplateName key="email-template-name" />,
    ],
    requiredFields: { value: true, templateId: true },
  },
};

export const getAutomationSettingsByCategory = (
  category: IPipelineCategory
) => {
  const categoryTypes = category.isSystem
    ? [EAutomationCategory.Applied]
    : category.testId
      ? [EAutomationCategory.Test, EAutomationCategory.All]
      : category.interviewId
        ? [EAutomationCategory.Interview, EAutomationCategory.All]
        : [EAutomationCategory.All];

  return Object.values(AUTOMATION_SETTINGS).filter((automation) =>
    categoryTypes.includes(automation.category)
  );
};

export const getAutomationSettingsGroupsByCategory = (
  category: IPipelineCategory
) => {
  const settingsByCategory = getAutomationSettingsByCategory(category);
  return Object.groupBy(settingsByCategory, ({ group }) => group);
};

export const getAutomationSettingsByType = (type: EAutomationType) => {
  return AUTOMATION_SETTINGS[type];
};

export const automationValidationSchema = object().shape({
  type: mixed<EAutomationType>().required(),
  stageId: string().required(),
  enabled: boolean().required(),
  destinationStageId: string().when('type', {
    is: (type: EAutomationType) =>
      getAutomationSettingsByType(type).requiredFields.destinationStageId,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
  }),
  templateId: string().when(['type', 'systemTemplateId'], {
    is: (type: EAutomationType, systemTemplateId: TId | null) =>
      !systemTemplateId &&
      getAutomationSettingsByType(type).requiredFields.templateId,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
  }),
  systemTemplateId: string().nullable(),
  value: number().when('type', {
    is: (type: EAutomationType) =>
      getAutomationSettingsByType(type).requiredFields.value,
    then: (schema) => schema.required().min(1),
    otherwise: (schema) => schema.nullable(),
  }),
});
